import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";
import {
  loadConsent,
  saveConsent,
  updateConsent,
  applyStoredConsent,
  acceptAll,
  rejectAll,
  DEFAULT_CONSENT,
} from "@/utils/consent";
import { X } from "lucide-react";

export function CookieBanner() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const stored = applyStoredConsent();
    if (!stored) {
      setVisible(true);
    } else {
      setPrefs(stored);
    }
  }, []);

  const handleAccept = () => {
    const c = acceptAll();
    setPrefs(c);
    setVisible(false);
    setPrefsOpen(false);
  };

  const handleReject = () => {
    const c = rejectAll();
    setPrefs(c);
    setVisible(false);
    setPrefsOpen(false);
  };

  const handleSave = () => {
    const c = { ...prefs, necessary: true };
    saveConsent(c);
    updateConsent(c);
    setVisible(false);
    setPrefsOpen(false);
  };

  const togglePref = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  return (
    <>
      <AnimatePresence>
        {visible && !prefsOpen && (
          <motion.div
            key="banner"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 lg:left-8 lg:bottom-8 lg:right-auto lg:max-w-md z-50"
            data-testid="cookie-banner"
          >
            <div className="rounded-2xl border border-slate-700/70 bg-[#0B132B]/95 backdrop-blur-xl p-5 lg:p-6 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
              <p className="eyebrow mb-3">{t("cookie.title")}</p>
              <p className="text-sm text-slate-300 leading-relaxed">{t("cookie.body")}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <button onClick={handleAccept} className="btn-emerald text-xs !py-2 !px-4" data-testid="cookie-banner-accept-button">
                  {t("cookie.accept")}
                </button>
                <button onClick={handleReject} className="btn-ghost text-xs !py-2 !px-4" data-testid="cookie-banner-reject-button">
                  {t("cookie.reject")}
                </button>
                <button onClick={() => setPrefsOpen(true)} className="text-xs text-slate-300 underline underline-offset-4 hover:text-emerald-400 transition-colors px-2 py-2" data-testid="cookie-banner-preferences-button">
                  {t("cookie.manage")}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {prefsOpen && (
          <motion.div
            key="prefs-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0B132B]/85 backdrop-blur-md flex items-end lg:items-center justify-center p-4"
            data-testid="cookie-preferences-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="w-full lg:max-w-2xl rounded-3xl border border-slate-700/70 bg-[#0F172A] p-6 lg:p-8 max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="eyebrow mb-2">{t("cookie.prefs_title")}</p>
                  <h3 className="font-display text-2xl font-bold">{t("cookie.title")}</h3>
                </div>
                <button
                  onClick={() => setPrefsOpen(false)}
                  className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center text-slate-300 hover:text-white"
                  aria-label={t("cookie.close")}
                  data-testid="cookie-preferences-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">{t("cookie.prefs_intro")}</p>

              <div className="space-y-3">
                <PrefRow
                  labelKey="necessary"
                  descKey="necessary_desc"
                  checked
                  disabled
                  onChange={() => {}}
                  testId="cookie-toggle-necessary"
                  t={t}
                />
                <PrefRow
                  labelKey="analytics"
                  descKey="analytics_desc"
                  checked={prefs.analytics}
                  onChange={() => togglePref("analytics")}
                  testId="cookie-toggle-analytics"
                  t={t}
                />
                <PrefRow
                  labelKey="marketing"
                  descKey="marketing_desc"
                  checked={prefs.marketing}
                  onChange={() => togglePref("marketing")}
                  testId="cookie-toggle-marketing"
                  t={t}
                />
                <PrefRow
                  labelKey="personalization"
                  descKey="personalization_desc"
                  checked={prefs.personalization}
                  onChange={() => togglePref("personalization")}
                  testId="cookie-toggle-personalization"
                  t={t}
                />
              </div>

              <div className="mt-8 flex flex-wrap gap-2 justify-end">
                <button onClick={handleReject} className="btn-ghost text-xs !py-2 !px-4" data-testid="cookie-preferences-reject-button">
                  {t("cookie.reject")}
                </button>
                <button onClick={handleSave} className="btn-ghost text-xs !py-2 !px-4" data-testid="cookie-preferences-save-button">
                  {t("cookie.save")}
                </button>
                <button onClick={handleAccept} className="btn-emerald text-xs !py-2 !px-4" data-testid="cookie-preferences-accept-button">
                  {t("cookie.accept")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function PrefRow({ labelKey, descKey, checked, onChange, disabled, testId, t }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-800 bg-[#0B132B] p-4">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-white">{t(`cookie.${labelKey}`)}</p>
        <p className="mt-1 text-xs text-slate-400 leading-relaxed">{t(`cookie.${descKey}`)}</p>
      </div>
      <label className={`relative inline-flex items-center cursor-pointer ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          data-testid={testId}
        />
        <span className="w-11 h-6 rounded-full bg-slate-700 peer-checked:bg-emerald-500 transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
      </label>
    </div>
  );
}

export default CookieBanner;
